import React from 'react'

/**
 * Illustrative salary-band structure: grades, ranges, midpoint progression and
 * the overlap between adjacent grades.
 *
 * Deliberately schematic. The axis is indexed to the lowest midpoint rather
 * than showing currency, so this illustrates the shape of a band architecture
 * without implying it is any particular client's real pay data.
 */

// index = midpoint relative to grade 1 (=100). spread = range width as % of midpoint.
const GRADES = [
  { name: 'G1', index: 100, spread: 40 },
  { name: 'G2', index: 122, spread: 44 },
  { name: 'G3', index: 150, spread: 48 },
  { name: 'G4', index: 186, spread: 52 },
  { name: 'G5', index: 232, spread: 56 },
  { name: 'G6', index: 292, spread: 60 }
]

const W = 620
const H = 330
const PAD_L = 54
const PAD_R = 30
const PAD_T = 34
const PAD_B = 46
const ROW = (H - PAD_T - PAD_B) / GRADES.length

// Domain covers the widest band, with headroom.
const MIN_V = 70
const MAX_V = 420
const xOf = (v) => PAD_L + ((v - MIN_V) / (MAX_V - MIN_V)) * (W - PAD_L - PAD_R)

export default function BandArchitecture({ className = '' }) {
  const rows = GRADES.map((g, i) => {
    const min = g.index * (1 - g.spread / 200)
    const max = g.index * (1 + g.spread / 200)
    // Drawn top-down so the highest grade sits at the top.
    const y = PAD_T + (GRADES.length - 1 - i) * ROW + ROW * 0.22
    return { ...g, min, max, y, h: ROW * 0.5 }
  })

  return (
    <svg
      viewBox={`0 0 ${W} ${H}`}
      className={className}
      role="img"
      aria-label="Schematic salary band architecture showing six grades, each with a pay range, midpoint, and overlap into the adjacent grade."
    >
      <defs>
        <linearGradient id="bandFill" x1="0" y1="0" x2="1" y2="0">
          <stop offset="0%" stopColor="#ffd118" stopOpacity="0.13" />
          <stop offset="55%" stopColor="#ffd118" stopOpacity="0.3" />
          <stop offset="100%" stopColor="#ffd118" stopOpacity="0.13" />
        </linearGradient>
      </defs>

      {/* vertical guides */}
      {[100, 150, 200, 250, 300, 350, 400].map((v) => (
        <g key={v}>
          <line
            x1={xOf(v)}
            y1={PAD_T - 8}
            x2={xOf(v)}
            y2={H - PAD_B + 4}
            stroke="rgba(255,255,255,0.07)"
            strokeWidth="1"
          />
          <text
            x={xOf(v)}
            y={H - PAD_B + 20}
            textAnchor="middle"
            fill="rgba(255,255,255,0.34)"
            fontSize="10"
            fontFamily="Open Sans, system-ui, sans-serif"
          >
            {v}
          </text>
        </g>
      ))}

      <text
        x={W / 2}
        y={H - 8}
        textAnchor="middle"
        fill="rgba(255,255,255,0.4)"
        fontSize="10.5"
        fontFamily="Open Sans, system-ui, sans-serif"
      >
        Midpoint index (grade 1 = 100)
      </text>

      {/* midpoint progression line, drawn under the bands */}
      <polyline
        points={rows.map((r) => `${xOf(r.index).toFixed(1)},${(r.y + r.h / 2).toFixed(1)}`).join(' ')}
        fill="none"
        stroke="rgba(255,209,24,0.4)"
        strokeWidth="1.5"
        strokeDasharray="4 4"
      />

      {rows.map((r) => (
        <g key={r.name}>
          <text
            x={PAD_L - 14}
            y={r.y + r.h / 2 + 4}
            textAnchor="end"
            fill="rgba(255,255,255,0.72)"
            fontSize="12"
            fontWeight="600"
            fontFamily="Open Sans, system-ui, sans-serif"
          >
            {r.name}
          </text>

          <rect
            x={xOf(r.min)}
            y={r.y}
            width={xOf(r.max) - xOf(r.min)}
            height={r.h}
            rx={r.h / 2}
            fill="url(#bandFill)"
            stroke="rgba(255,209,24,0.42)"
            strokeWidth="1"
          />

          {/* midpoint marker */}
          <line
            x1={xOf(r.index)}
            y1={r.y - 3}
            x2={xOf(r.index)}
            y2={r.y + r.h + 3}
            stroke="#ffd118"
            strokeWidth="2"
            strokeLinecap="round"
          />

          <text
            x={xOf(r.max) + 8}
            y={r.y + r.h / 2 + 3.5}
            fill="rgba(255,255,255,0.45)"
            fontSize="10"
            fontFamily="Open Sans, system-ui, sans-serif"
          >
            {r.spread}%
          </text>
        </g>
      ))}

      {/* legend */}
      <g transform={`translate(${PAD_L}, ${PAD_T - 18})`}>
        <rect x="0" y="-8" width="16" height="9" rx="4.5" fill="url(#bandFill)" stroke="rgba(255,209,24,0.42)" />
        <text x="22" y="0" fill="rgba(255,255,255,0.5)" fontSize="10.5" fontFamily="Open Sans, system-ui, sans-serif">
          range
        </text>
        <line x1="72" y1="-9" x2="72" y2="1" stroke="#ffd118" strokeWidth="2" strokeLinecap="round" />
        <text x="80" y="0" fill="rgba(255,255,255,0.5)" fontSize="10.5" fontFamily="Open Sans, system-ui, sans-serif">
          midpoint
        </text>
        <text x="146" y="0" fill="rgba(255,255,255,0.5)" fontSize="10.5" fontFamily="Open Sans, system-ui, sans-serif">
          % = range spread
        </text>
      </g>
    </svg>
  )
}
